import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const PROFILE_STATUSES = ['active', 'inactive'];

export default function PlatformBillingProfileFormPage() {
  const { tenantId: tenantIdParam } = useParams();
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isNew = !tenantIdParam;
  const isSuper = user?.role === 'super_admin';

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(tenantIdParam || '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [vat, setVat] = useState('');
  const [invRef, setInvRef] = useState('');
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isNew || !isSuper) return undefined;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setTenants(json.tenants || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, isSuper]);

  const load = useCallback(async () => {
    if (!tenantIdParam) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/billing/profiles/tenant/${tenantIdParam}`);
      const json = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setName('');
        setEmail('');
        setPhone('');
        setAddress('');
        setVat('');
        setInvRef('');
        setStatus('active');
        setTenantId(tenantIdParam);
        return;
      }
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const p = json.profile;
      setTenantId(String(p.tenant_id));
      setName(p.billing_contact_name || '');
      setEmail(p.billing_contact_email || '');
      setPhone(p.billing_contact_phone || '');
      setAddress(p.billing_address || '');
      setVat(p.vat_number || '');
      setInvRef(p.invoice_reference || '');
      setStatus(p.status || 'active');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantIdParam]);

  useEffect(() => {
    if (tenantIdParam) load();
  }, [load, tenantIdParam]);

  useEffect(() => {
    if (tenantIdParam) setTenantId(tenantIdParam);
  }, [tenantIdParam]);

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuper && isNew) {
    return <Navigate to="/platform/billing/profiles" replace />;
  }

  const save = async (e) => {
    e.preventDefault();
    const tid = String(tenantId || tenantIdParam || '').trim();
    if (!tid) {
      setError('Select a tenant');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await platformApiFetch(`/api/platform/billing/profiles/tenant/${tid}`, {
        method: 'PUT',
        body: {
          billing_contact_name: name.trim(),
          billing_contact_email: email.trim(),
          billing_contact_phone: phone.trim() || null,
          billing_address: address.trim() || null,
          vat_number: vat.trim() || null,
          invoice_reference: invRef.trim() || null,
          status
        }
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) throw new Error(json.error || 'Forbidden');
      if (!res.ok) throw new Error(json.error || 'Save failed');
      setSuccess(true);
      if (isNew) navigate(`/platform/billing/profiles/tenant/${tid}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const readOnly = !isSuper;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">{isNew ? 'New billing profile' : `Billing profile · tenant ${tenantId}`}</Typography>
        <Button variant="outlined" component={Link} to="/platform/billing/profiles">
          Back to list
        </Button>
      </Box>
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only for your role. Super admins can edit billing profiles.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Saved.
        </Alert>
      )}
      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={save}>
          {isNew ? (
            <FormControl fullWidth margin="normal" required disabled={readOnly}>
              <InputLabel>Tenant</InputLabel>
              <Select
                label="Tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select tenant</em>
                </MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.tenant_name} (#{t.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField fullWidth margin="normal" label="Tenant ID" value={tenantId} disabled />
          )}
          <TextField
            fullWidth
            required
            label="Billing contact name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            disabled={readOnly}
          />
          <TextField
            fullWidth
            required
            type="email"
            label="Billing email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            disabled={readOnly}
          />
          <TextField
            fullWidth
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            margin="normal"
            disabled={readOnly}
          />
          <TextField
            fullWidth
            label="Billing address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            margin="normal"
            multiline
            minRows={3}
            disabled={readOnly}
          />
          <TextField fullWidth label="VAT / tax ID" value={vat} onChange={(e) => setVat(e.target.value)} margin="normal" disabled={readOnly} />
          <TextField
            fullWidth
            label="Invoice / PO reference"
            value={invRef}
            onChange={(e) => setInvRef(e.target.value)}
            margin="normal"
            disabled={readOnly}
          />
          <FormControl fullWidth margin="normal" disabled={readOnly}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {PROFILE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isSuper && (
            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" disabled={saving || !name.trim() || !email.trim() || !tenantId}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
